import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Global 401 handler: clear token and redirect to /login
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || "";
      // Don't auto-redirect on login attempts
      if (!url.includes("/auth/login") && !url.includes("/auth/me")) {
        localStorage.removeItem("flotteops_token");
        delete api.defaults.headers.common["Authorization"];
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export const fetchTrucks = () => api.get("/trucks").then((r) => r.data);
export const fetchTruck = (id) => api.get(`/trucks/${id}`).then((r) => r.data);
export const createTruck = (data) => api.post("/trucks", data).then((r) => r.data);
export const updateTruck = (id, data) => api.patch(`/trucks/${id}`, data).then((r) => r.data);
export const geocodeAddress = (q) => api.get("/geocode", { params: { q } }).then((r) => r.data);
export const suggestAddress = (q) => api.get("/geocode/suggest", { params: { q } }).then((r) => r.data);
export const deleteTruck = (id) => api.delete(`/trucks/${id}`).then((r) => r.data);
export const fetchStats = () => api.get("/stats").then((r) => r.data);

export const addPhoto = (id, dataUrl) =>
  api.post(`/trucks/${id}/photos`, { photo: dataUrl }).then((r) => r.data);
export const deletePhoto = (id, index) =>
  api.delete(`/trucks/${id}/photos/${index}`).then((r) => r.data);

export const fetchImpacts = (id) => api.get(`/trucks/${id}/impacts`).then((r) => r.data);
export const createImpact = (id, data) =>
  api.post(`/trucks/${id}/impacts`, data).then((r) => r.data);
export const updateImpact = (impactId, data) =>
  api.patch(`/impacts/${impactId}`, data).then((r) => r.data);
export const deleteImpact = (impactId) =>
  api.delete(`/impacts/${impactId}`).then((r) => r.data);

export const fetchComments = (id) => api.get(`/trucks/${id}/comments`).then((r) => r.data);
export const createComment = (id, data) =>
  api.post(`/trucks/${id}/comments`, data).then((r) => r.data);
export const deleteComment = (commentId) =>
  api.delete(`/comments/${commentId}`).then((r) => r.data);

export const fetchHistory = (id) => api.get(`/trucks/${id}/history`).then((r) => r.data);

export const fetchDrivers = () => api.get("/drivers").then((r) => r.data);
export const fetchDriverNames = () => api.get("/drivers/names").then((r) => r.data);
export const fetchDriversStats = () => api.get("/drivers/stats").then((r) => r.data);
export const createDriver = (data) => api.post("/drivers", data).then((r) => r.data);
export const updateDriver = (id, data) =>
  api.patch(`/drivers/${id}`, data).then((r) => r.data);
export const deleteDriver = (id) =>
  api.delete(`/drivers/${id}`).then((r) => r.data);

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const uploadPhoto = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/upload-photo", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data; // { path, size, content_type }
};

/**
 * Resolve a stored photo reference (storage path OR legacy base64 data URL)
 * into a renderable <img src>.
 */
export const photoUrl = (ref) => {
  if (!ref) return "";
  if (ref.startsWith("data:")) return ref; // legacy base64
  if (ref.startsWith("http")) return ref; // already absolute
  return `${API}/files/${ref}`;
};

// --- Appointments / Calendrier ---
export const fetchAppointments = (truckId) =>
  api.get(`/appointments${truckId ? `?truck_id=${truckId}` : ""}`).then((r) => r.data);
export const createAppointment = (data) =>
  api.post(`/appointments`, data).then((r) => r.data);
export const updateAppointment = (id, data) =>
  api.patch(`/appointments/${id}`, data).then((r) => r.data);
export const deleteAppointment = (id) =>
  api.delete(`/appointments/${id}`).then((r) => r.data);

// --- Claims / Sinistres ---
export const fetchClaims = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`/claims${qs ? "?" + qs : ""}`).then((r) => r.data);
};
export const createClaim = (data) => api.post(`/claims`, data).then((r) => r.data);
export const updateClaim = (id, data) =>
  api.patch(`/claims/${id}`, data).then((r) => r.data);
export const deleteClaim = (id) => api.delete(`/claims/${id}`).then((r) => r.data);

// --- Documents alerts ---
export const fetchDocumentAlerts = () =>
  api.get(`/alerts/documents`).then((r) => r.data);
export const fetchDocumentsOverview = (site) =>
  api.get(`/documents/overview${site ? `?site=${site}` : ""}`).then((r) => r.data);

// --- Plate duplicate / similarity check ---
export const checkPlate = (plate, excludeId) =>
  api
    .get(`/trucks/check-plate?plate=${encodeURIComponent(plate)}${excludeId ? `&exclude_id=${excludeId}` : ""}`)
    .then((r) => r.data);

// --- Daily driver-truck assignments ---
export const fetchAssignments = (date, site) => {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (site) params.set("site", site);
  const qs = params.toString();
  return api.get(`/assignments${qs ? `?${qs}` : ""}`).then((r) => r.data);
};
export const autoAssign = (date, drivers, site) =>
  api.post(`/assignments/auto`, { date, drivers, site }).then((r) => r.data);
export const saveAssignments = (date, assignments, site) =>
  api.post(`/assignments/save`, { date, assignments, site }).then((r) => r.data);
export const deleteAssignment = (id) =>
  api.delete(`/assignments/${id}`).then((r) => r.data);
export const fetchDriverHabits = (date) => {
  const qs = date ? `?date=${date}` : "";
  return api.get(`/assignments/driver-habits${qs}`).then((r) => r.data);
};
export const fetchTruckUsageHistory = (truckId) =>
  api.get(`/trucks/${truckId}/usage-history`).then((r) => r.data);

// --- Bonus penalties ---
export const fetchPenalties = (driver, activeOnly = false) => {
  const params = new URLSearchParams();
  if (driver) params.set("driver", driver);
  if (activeOnly) params.set("active_only", "true");
  const qs = params.toString();
  return api.get(`/penalties${qs ? `?${qs}` : ""}`).then((r) => r.data);
};
export const createPenalty = (data) =>
  api.post(`/penalties`, data).then((r) => r.data);
export const updatePenalty = (id, data) =>
  api.patch(`/penalties/${id}`, data).then((r) => r.data);
export const deletePenalty = (id) =>
  api.delete(`/penalties/${id}`).then((r) => r.data);
export const fetchPenaltiesMonthly = (month, site) => {
  const params = new URLSearchParams();
  params.set("month", month);
  if (site) params.set("site", site);
  return api.get(`/penalties/monthly?${params.toString()}`).then((r) => r.data);
};
export const bulkDeleteDrivers = (ids) =>
  api.post(`/drivers/bulk-delete`, { ids }).then((r) => r.data);

export const extractVinFromCarteGrise = (truckId) =>
  api.post(`/trucks/${truckId}/extract-vin`).then((r) => r.data);

export const extractDocumentExpiry = (truckId, docType) =>
  api
    .post(`/trucks/${truckId}/documents/${docType}/extract-expiry`)
    .then((r) => r.data);

export const fetchBudgetOverview = (month, site) => {
  const params = new URLSearchParams();
  params.set("month", month);
  if (site) params.set("site", site);
  return api.get(`/budget/overview?${params.toString()}`).then((r) => r.data);
};

const _budgetHistoryCache = new Map();
export const fetchBudgetHistory = (year, site) => {
  const params = new URLSearchParams();
  params.set("year", year);
  if (site) params.set("site", site);
  const key = params.toString();
  const hit = _budgetHistoryCache.get(key);
  if (hit && Date.now() - hit.at < 5000) return hit.promise;
  const promise = api.get(`/budget/history?${key}`).then((r) => r.data);
  promise.catch(() => _budgetHistoryCache.delete(key));
  _budgetHistoryCache.set(key, { at: Date.now(), promise });
  return promise;
};

export const fetchTruckBreakdownEvents = (truckId) =>
  api.get(`/trucks/${truckId}/breakdown-events`).then((r) => r.data);


// --- Fuel / Consommation ---
export const fetchFuelCards = () => api.get(`/fuel/cards`).then((r) => r.data);
export const createFuelCard = (data) =>
  api.post(`/fuel/cards`, data).then((r) => r.data);
export const updateFuelCard = (id, data) =>
  api.patch(`/fuel/cards/${id}`, data).then((r) => r.data);
export const deleteFuelCard = (id) =>
  api.delete(`/fuel/cards/${id}`).then((r) => r.data);
export const resetFuelCardChecks = () =>
  api.post(`/fuel/cards/reset-checks`).then((r) => r.data);

export const fetchFuelEntries = (truckId) => {
  const qs = truckId ? `?truck_id=${truckId}` : "";
  return api.get(`/fuel/entries${qs}`).then((r) => r.data);
};
export const createFuelEntry = (data) =>
  api.post(`/fuel/entries`, data).then((r) => r.data);
export const updateFuelEntry = (id, data) =>
  api.patch(`/fuel/entries/${id}`, data).then((r) => r.data);
export const deleteFuelEntry = (id) =>
  api.delete(`/fuel/entries/${id}`).then((r) => r.data);

export const fetchFuelStats = () => api.get(`/fuel/stats`).then((r) => r.data);
export const fetchFuelSettings = () => api.get(`/fuel/settings`).then((r) => r.data);
export const updateFuelSettings = (threshold) =>
  api.put(`/fuel/settings`, { threshold_l_per_100: threshold }).then((r) => r.data);

// --- Amendes ---
export const fetchFines = () => api.get(`/fines`).then((r) => r.data);
export const createFine = (data) => api.post(`/fines`, data).then((r) => r.data);
export const updateFine = (id, data) =>
  api.patch(`/fines/${id}`, data).then((r) => r.data);
export const toggleFinePaid = (id) =>
  api.post(`/fines/${id}/toggle-paid`).then((r) => r.data);
export const deleteFine = (id) =>
  api.delete(`/fines/${id}`).then((r) => r.data);
export const fetchFinesStats = () => api.get(`/fines/stats`).then((r) => r.data);

// --- Notes / Rappels de RDV ---
export const fetchNotes = () => api.get(`/notes`).then((r) => r.data);
export const createNote = (text, truck_id = null) =>
  api.post(`/notes`, { text, truck_id }).then((r) => r.data);
export const updateNote = (id, data) => api.patch(`/notes/${id}`, data).then((r) => r.data);
export const deleteNote = (id) => api.delete(`/notes/${id}`).then((r) => r.data);

// --- Wave Plan (OCR + shared today's plan) ---
export const ocrWavePlan = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post(`/wave-plan/ocr`, fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};
export const importWavePlanExcel = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post(`/wave-plan/import-excel`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const fetchWavePlanToday = () =>
  api.get(`/wave-plan/today`).then((r) => r.data);
export const saveWavePlanToday = (state) =>
  api.put(`/wave-plan/today`, state).then((r) => r.data);




// --- Chat ---
export const fetchChatConversations = () =>
  api.get(`/chat/conversations`).then((r) => r.data);
export const fetchChatMessages = (channel = "general", peerId = null, limit = 100) => {
  const params = new URLSearchParams({ channel, limit: String(limit) });
  if (peerId) params.set("peer_id", peerId);
  return api.get(`/chat/messages?${params.toString()}`).then((r) => r.data);
};
export const sendChatMessage = (data) =>
  api.post(`/chat/messages`, data).then((r) => r.data);
export const markChatRead = (channel = "general", peerId = null) => {
  const params = new URLSearchParams({ channel });
  if (peerId) params.set("peer_id", peerId);
  return api.post(`/chat/read?${params.toString()}`).then((r) => r.data);
};

// --- Notifications (today) ---
export const fetchNotificationsToday = () =>
  api.get(`/notifications/today`).then((r) => r.data);

// --- Télépéages (DLP4) ---
export const fetchTollTags = () => api.get("/toll-tags").then((r) => r.data);
export const fetchTollTagForTruck = (truckId) => api.get(`/toll-tags/by-truck/${truckId}`).then((r) => r.data);
export const createTollTag = (data) => api.post("/toll-tags", data).then((r) => r.data);
export const updateTollTag = (id, data) => api.patch(`/toll-tags/${id}`, data).then((r) => r.data);
export const deleteTollTag = (id) => api.delete(`/toll-tags/${id}`).then((r) => r.data);
export const fetchFuelAnalytics = (month, site) =>
  api.get("/fuel/analytics", { params: { month, site: site && site !== "all" ? site : undefined } }).then((r) => r.data);
